// content/zones/zone3.ts — THE PALE COURT's zone mechanic.
//
//   "the dead don't stay dead — every kill leaves a husk that re-forms unless
//    you seal it, and something is racing you to the husks."  (DESIGN §5 Zone 3)
//
// The kill loop inverts here. In every earlier room a kill was a subtraction;
// in the Pale Court a kill is a *job*: the body leaves a pale husk, the husk
// re-forms the enemy at full health after four seconds, and one hit anywhere on
// it seals it for good (+1 ember). Gravetenders make the job a race — they
// ignore the Warden entirely, sprint to the nearest husk, and tend it down to a
// 1.5-second re-form that comes back Frenzied. Every resurrection darkens the
// zone by 3% of the Gloom, so sloppiness is visible on the meter.
//
// ---------------------------------------------------------------------------
// FLAGGED IMPLEMENTATION — husks ride the enemy pool.
//
// A husk is a real EnemyDef ('pale_husk', 1 HP, speed 0, no touch damage, zero
// embers) with a custom brain. That is the only way "one hit seals it" is true
// of *every* weapon, splash, burn trail, shield shove and Emberburst without
// touching a line of shared code — they all already know how to hit an enemy.
// Every husk is tagged `gloomspawn` on spawn, which by the frozen tag contract
// means: never counts toward the room-clear condition, never blocks a clear,
// never pays. The one consequence, accepted and stated: rooms.ts dissolves
// every gloomspawn-tagged body at the moment a room clears, so an un-sealed
// husk left when the last real enemy dies dissolves with the room instead of
// re-forming. The room is over; the race was the point.
// ---------------------------------------------------------------------------

import type { Draw, EliteRider, Room, RunCtx, Vec, ZoneMechanic } from '../../types'
import {
  ZONE3,
  ZONE3_BOSS,
  ZONE3_BOSS_ARENA,
  ZONE3_ENEMIES,
  ZONE3_GENERATORS,
  ZONE3_PROJECTILES,
  ZONE3_REFORMED,
  ZONE3_RULES,
} from '../../data/zones/zone3'
import { registerZone, registerZoneContent } from '../../game/zones'
import { moveEnemy, pursue, registerEnemyBrain } from '../../game/enemies'
import { emit } from '../../engine/particles'
import { fx } from '../../engine/fx'
import { PixelArt, registerPainter, registerRecipe, shade } from '../../engine/sprites'
import type { EnemyEnt } from '../../game/entities'
import type { Run } from '../../game/game'

const R = ZONE3_RULES
const HUSK = R.huskId
const TENDER = 'gravetender'
const TAU = Math.PI * 2

/** RunCtx plus the two flag bags the run keeps for zone mechanics to read. */
type RunLike = RunCtx & {
  readonly flags?: Record<string, number | boolean>
  readonly relicFlags?: Record<string, number>
}

// Scratch reused by everything below — nothing in this file allocates per frame.
const dirScratch: Vec = { x: 0, y: 0 }
const sepScratch: Vec = { x: 0, y: 0 }
const posScratch: Vec = { x: 0, y: 0 }

function num(v: unknown, d: number): number {
  return typeof v === 'number' ? v : d
}

// ===========================================================================
// zone state
// ===========================================================================

const state = {
  /** The live run, cached for the render hooks (which only receive a Draw). */
  ctx: null as RunCtx | null,
  /** Grave Candle (talisman): the first husk of each room seals itself. */
  candleReady: false,
  saidHusk: false,
  saidTender: false,
  saidReform: false,
  /** Room counters, for barks and for the render hooks' cheap early-out. */
  huskCount: 0,
  sealedThisRoom: 0,
  reformedThisRoom: 0,
}

/** Gravetender identity, so two tenders do not pile onto the same husk. */
let tenderSeq = 0

function isArena(ctx: RunCtx): boolean {
  return ctx.room.template.id === ZONE3_BOSS_ARENA.id
}

/** The throne hall runs a tighter clock: untended husks come back in 3s. */
function huskSeconds(ctx: RunCtx): number {
  return isArena(ctx) ? ZONE3_BOSS.huskSeconds : R.huskSeconds
}

// ===========================================================================
// husks — creation, sealing, re-forming
// ===========================================================================

/** A husk was destroyed by a blow. That is a seal: it pays, and it stays down. */
function seal(ctx: RunCtx, x: number, y: number, tended: boolean): void {
  const bonus = (ctx as RunLike).relicFlags?.huskSealEmberBonus ?? 0
  ctx.addEmbers(R.sealEmbers + bonus)
  ctx.featTick('huskSealed')
  ctx.sfx('pickup')
  state.sealedThisRoom++
  fx.floatText('SEALED', x, y - 20, '#dce7ff', 11)
  emit(x, y, {
    count: 16,
    color: '#dce7ff',
    speed: 150,
    speedVar: 90,
    life: 0.45,
    lifeVar: 0.2,
    size: 3,
    shape: 1,
  })
  emit(x, y, { count: 6, color: tended ? '#c79bff' : '#8f9ec4', speed: 70, life: 0.6, size: 2, shape: 1 })
}

/** Lay a husk where a body fell. Returns false if the def is missing. */
function layHusk(ctx: RunCtx, x: number, y: number, srcId: string, elite: EliteRider | null): boolean {
  posScratch.x = x
  posScratch.y = y
  const h = ctx.spawnEnemy(HUSK, posScratch, { elite: null, silent: true }) as EnemyEnt
  if (!h.alive) return false
  const t = huskSeconds(ctx)
  // The tag contract (types.ts): gloomspawn = excluded from room-clear and pays
  // nothing. A husk is a job on the floor, never a body holding the room.
  h.tags.gloomspawn = true
  h.tags.noKnockback = true
  h.tags.stunProof = true // a stun must never pause the seal/reform brain (D-031)
  h.tags.huskT = t
  h.tags.huskMax = t
  h.tags.srcId = srcId
  h.tags.srcElite = elite ?? ''
  emit(x, y, {
    count: 14,
    color: '#dce7ff',
    speed: 60,
    speedVar: 40,
    life: 0.7,
    lifeVar: 0.25,
    size: 3,
    shape: 1,
  })
  ctx.sfx('ui')
  if (!state.saidHusk) {
    state.saidHusk = true
    ctx.bark('A husk. One blow seals it — or it gets back up whole.')
  }
  return true
}

/**
 * The timer ran out. The body comes back at full health, once: the new body
 * carries `reformed`, and the death of a reformed body leaves nothing. Tended
 * husks come back Frenzied; plain ones keep whatever rider they died with.
 */
function reform(run: Run, e: EnemyEnt): void {
  const srcId = typeof e.tags.srcId === 'string' ? e.tags.srcId : ''
  const tended = e.tags.tended === true
  const carried = typeof e.tags.srcElite === 'string' && e.tags.srcElite ? (e.tags.srcElite as EliteRider) : null
  const elite: EliteRider | null = tended ? 'frenzied' : carried
  posScratch.x = e.pos.x
  posScratch.y = e.pos.y

  e.kill(true) // silent: a husk's own expiry is not a kill and leaves no husk

  if (srcId) {
    // the re-formed twin: identical body, zero payout (data/zones/zone3.ts)
    const twin = srcId + R.reformedSuffix
    const id = run.enemyDef(twin) ? twin : srcId
    const born = run.spawnEnemy(id, posScratch, { elite, silent: true })
    if (born.alive) born.tags.reformed = true
  }

  // DESIGN §5: each resurrection is +3% Gloom. Sloppiness darkens the zone.
  run.gloom.add(R.reformGloom)
  state.reformedThisRoom++
  run.sfx('door')
  run.shake(2.5, 0.22)
  emit(posScratch.x, posScratch.y, {
    count: 22,
    color: tended ? '#ff5a3c' : '#dce7ff',
    speed: 200,
    speedVar: 140,
    life: 0.5,
    lifeVar: 0.2,
    size: 4,
    shape: 1,
  })
  if (!state.saidReform) {
    state.saidReform = true
    run.bark('It stood back up. The dark took the fee.')
  }
}

// ===========================================================================
// brains
// ===========================================================================

/** Cheap local separation — game/enemies.ts keeps its own version private. */
function localSep(run: Run, e: EnemyEnt, out: Vec): void {
  out.x = 0
  out.y = 0
  const list = run.enemyPool.live
  const reach = e.radius * 2.4
  run.hash.query(e.pos.x, e.pos.y, reach, (i) => {
    const o = list[i]
    if (o === e || !o.alive) return
    const dx = e.pos.x - o.pos.x
    const dy = e.pos.y - o.pos.y
    const d2 = dx * dx + dy * dy
    const want = e.radius + o.radius
    if (d2 >= want * want) return
    if (d2 < 0.0001) {
      out.x += 0.7
      out.y -= 0.3
      return
    }
    const d = Math.sqrt(d2)
    const push = (want - d) / want
    out.x += (dx / d) * push
    out.y += (dy / d) * push
  })
}

// --- the husk: a countdown with a hitbox -----------------------------------

registerEnemyBrain('z3-husk', (run: Run, e: EnemyEnt, dt: number): void => {
  e.vel.x = 0
  e.vel.y = 0

  // Sexton's Spade (relic flag, game/relics.ts): walking over a husk seals it,
  // no input. The relic flag is the whole contract — the zone never learns
  // which relic set it.
  if ((run.relicFlags.huskAutoSealOnWalk ?? 0) > 0) {
    const dx = run.player.pos.x - e.pos.x
    const dy = run.player.pos.y - e.pos.y
    const reach = e.radius + run.player.radius + R.walkSealSlack
    if (dx * dx + dy * dy <= reach * reach) {
      e.kill() // non-silent: routes through onEnemyDeath, which is the seal
      return
    }
  }

  const t = num(e.tags.huskT, 0) - dt
  e.tags.huskT = t
  if (t > 0) return
  reform(run, e)
})

// --- Courtier Blade: the two-step lunge behind a red glint ------------------

const C_APPROACH = 0
const C_WINDUP = 1
const C_STEP = 2
const C_RECOVER = 3

function courtierStep(run: Run, e: EnemyEnt): void {
  const dx = run.player.pos.x - e.pos.x
  const dy = run.player.pos.y - e.pos.y
  const l = Math.hypot(dx, dy) || 1
  e.tags.lx = dx / l
  e.tags.ly = dy / l
  e.stateT = 0
  e.facing = dx < 0 ? -1 : 1
}

function courtierStrike(run: Run, e: EnemyEnt): void {
  const ai = e.def.ai
  const arc = num(ai.strikeArc, 1.3)
  const range = num(ai.strikeRange, 52)
  const mul = num(ai.strikeDamage, 1)
  const lx = num(e.tags.lx, 1)
  const ly = num(e.tags.ly, 0)
  const dir = Math.atan2(ly, lx)
  const pdx = run.player.pos.x - e.pos.x
  const pdy = run.player.pos.y - e.pos.y
  const pd = Math.hypot(pdx, pdy)
  let inArc = pd <= range + run.player.radius + e.radius
  if (inArc && pd > 2) {
    let a = Math.atan2(pdy, pdx) - dir
    while (a > Math.PI) a -= TAU
    while (a < -Math.PI) a += TAU
    inArc = Math.abs(a) <= arc / 2
  }
  if (inArc) run.damagePlayer(e.touchDamage * mul, e.def.id)
  run.sfx('swing')
  emit(e.pos.x + lx * 22, e.pos.y + ly * 22, {
    count: 5,
    color: '#ff8a6a',
    speed: 140,
    life: 0.16,
    size: 2,
    angle: dir,
    spread: arc,
    shape: 1,
  })
}

registerEnemyBrain('z3-courtier', (run: Run, e: EnemyEnt, dt: number, speedMul: number): void => {
  const ai = e.def.ai
  const lungeRange = num(ai.lungeRange, 156)
  const cd = num(ai.lungeCooldown, 2.5)
  const windup = num(ai.windup, 0.46)
  const steps = num(ai.steps, 2)
  const stepSpeed = num(ai.stepSpeed, 430)
  const stepTime = num(ai.stepTime, 0.18)
  const stepGap = num(ai.stepGap, 0.15)
  const recover = num(ai.recover, 0.45)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy)
  localSep(run, e, sepScratch)

  if (e.state === C_APPROACH) {
    pursue(run, e, dirScratch)
    const sp = e.speed * speedMul
    moveEnemy(run, e, (dirScratch.x + sepScratch.x * 1.4) * sp, (dirScratch.y + sepScratch.y * 1.4) * sp, dt)
    if (
      dist < lungeRange &&
      e.cool <= 0 &&
      e.spawnGrace <= 0 &&
      run.world.lineOfSight(e.pos.x, e.pos.y, px, py)
    ) {
      e.state = C_WINDUP
      e.stateT = 0
      e.windup = windup
      e.telegraph = windup
      e.tags.glint = true
      run.sfx('telegraph')
    }
    return
  }

  if (e.state === C_WINDUP) {
    e.windup -= dt
    // settle back onto the rear foot — the pose that sells the lunge
    moveEnemy(run, e, -dx * 0.22 + sepScratch.x * 30, -dy * 0.22 + sepScratch.y * 30, dt)
    if (Math.abs(dx) > 4) e.facing = dx < 0 ? -1 : 1
    if (e.windup <= 0) {
      e.tags.glint = false
      e.state = C_STEP
      e.tags.stepsDone = 0
      e.tags.gapT = 0
      courtierStep(run, e)
    }
    return
  }

  if (e.state === C_STEP) {
    const gap = num(e.tags.gapT, 0)
    if (gap > 0) {
      e.tags.gapT = gap - dt
      moveEnemy(run, e, 0, 0, dt)
      if (gap - dt <= 0) courtierStep(run, e)
      return
    }
    const lx = num(e.tags.lx, 1)
    const ly = num(e.tags.ly, 0)
    moveEnemy(run, e, lx * stepSpeed, ly * stepSpeed, dt)
    if (e.stateT > stepTime) {
      courtierStrike(run, e)
      const done = num(e.tags.stepsDone, 0) + 1
      e.tags.stepsDone = done
      if (done >= steps) {
        e.state = C_RECOVER
        e.stateT = 0
      } else {
        e.tags.gapT = stepGap
      }
    }
    return
  }

  // recover
  moveEnemy(run, e, sepScratch.x * 40, sepScratch.y * 40, dt)
  if (e.stateT > recover) {
    e.state = C_APPROACH
    e.stateT = 0
    e.cool = cd
  }
})

// --- Wailer: expanding rings of grief ---------------------------------------

const W_HOLD = 0
const W_WINDUP = 1
const W_CAST = 2
const W_RECOVER = 3

function fireRing(run: Run, e: EnemyEnt, index: number): void {
  const ai = e.def.ai
  const shards = Math.max(3, Math.round(num(ai.shards, 9)))
  const projId = typeof ai.projectile === 'string' ? ai.projectile : ''
  if (!projId) return
  // Ring 2 is offset by half a gap, so its shards fall in ring 1's holes: near
  // the Wailer the wall is solid, far out the gaps open. The counterplay is
  // legible from the first ring — get out, or thread it early.
  const off = (index % 2) * (Math.PI / shards)
  for (let i = 0; i < shards; i++) {
    const a = off + (i / shards) * TAU
    run.spawnProjectileAt(projId, e.pos.x, e.pos.y - 4, Math.cos(a), Math.sin(a), false)
  }
  run.sfx('shoot')
  emit(e.pos.x, e.pos.y - 4, {
    count: 12,
    color: '#a8e878',
    speed: 110,
    life: 0.35,
    size: 3,
    shape: 1,
  })
}

registerEnemyBrain('z3-wailer', (run: Run, e: EnemyEnt, dt: number, speedMul: number): void => {
  const ai = e.def.ai
  const keep = num(ai.keepRange, 250)
  const minRange = num(ai.minRange, 150)
  const fireRange = num(ai.fireRange, 330)
  const cd = num(ai.fireCooldown, 4.2)
  const windup = num(ai.windup, 0.58)
  const rings = Math.max(1, Math.round(num(ai.rings, 2)))
  const ringGap = num(ai.ringGap, 0.5)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1
  localSep(run, e, sepScratch)

  if (e.state === W_WINDUP) {
    e.windup -= dt
    moveEnemy(run, e, sepScratch.x * 20, sepScratch.y * 20, dt)
    if (e.windup <= 0) {
      e.state = W_CAST
      e.stateT = 0
      e.tags.ringsLeft = rings - 1
      e.tags.ringT = ringGap
      fireRing(run, e, 0)
    }
    return
  }

  if (e.state === W_CAST) {
    moveEnemy(run, e, sepScratch.x * 20, sepScratch.y * 20, dt)
    const left = num(e.tags.ringsLeft, 0)
    const t = num(e.tags.ringT, 0) - dt
    e.tags.ringT = t
    if (t <= 0) {
      if (left > 0) {
        fireRing(run, e, rings - left)
        e.tags.ringsLeft = left - 1
        e.tags.ringT = ringGap
      } else {
        e.state = W_RECOVER
        e.stateT = 0
        e.cool = cd
      }
    }
    return
  }

  if (e.state === W_RECOVER) {
    moveEnemy(run, e, sepScratch.x * 26, sepScratch.y * 26, dt)
    if (e.stateT > 0.4) {
      e.state = W_HOLD
      e.stateT = 0
    }
    return
  }

  // hold the band: mourners drift, they do not charge
  const los = run.world.lineOfSight(e.pos.x, e.pos.y, px, py)
  const sp = e.speed * speedMul
  let mx = 0
  let my = 0
  if (dist < minRange) {
    mx = -(dx / dist)
    my = -(dy / dist)
  } else if (dist > keep || !los) {
    pursue(run, e, dirScratch)
    mx = dirScratch.x
    my = dirScratch.y
  } else {
    const s = e.bob * 0.6 + e.pos.y * 0.01
    mx = (-dy / dist) * Math.sin(s)
    my = (dx / dist) * Math.sin(s)
  }
  moveEnemy(run, e, (mx + sepScratch.x * 1.2) * sp, (my + sepScratch.y * 1.2) * sp, dt)
  if (Math.abs(dx) > 6) e.facing = dx < 0 ? -1 : 1

  if (e.cool <= 0 && e.spawnGrace <= 0 && dist < fireRange && los) {
    e.state = W_WINDUP
    e.stateT = 0
    e.windup = windup
    e.telegraph = windup
    run.sfx('telegraph')
  }
})

// --- Gravetender: it is not here for you ------------------------------------

registerEnemyBrain('z3-gravetender', (run: Run, e: EnemyEnt, dt: number, speedMul: number): void => {
  const ai = e.def.ai
  const sprintMul = num(ai.sprintMul, 1.55)
  const tendRange = num(ai.tendRange, 26)
  const bandMin = num(ai.bandMin, 190)
  const bandMax = num(ai.bandMax, 285)
  const claimPenalty = num(ai.claimPenalty, 420)

  if (typeof e.tags.tid !== 'number') e.tags.tid = ++tenderSeq
  const tid = e.tags.tid as number

  // the nearest untended husk, with a penalty for one another tender is on
  let best: EnemyEnt | null = null
  let bestScore = Infinity
  const list = run.enemyPool.live
  for (let i = 0; i < list.length; i++) {
    const o = list[i]
    if (!o.alive || o.def.id !== HUSK || o.tags.tended === true) continue
    let sc = Math.hypot(o.pos.x - e.pos.x, o.pos.y - e.pos.y)
    if (num(o.tags.tendClaimT, -1) > run.time && o.tags.tendBy !== tid) sc += claimPenalty
    if (sc < bestScore) {
      bestScore = sc
      best = o
    }
  }

  localSep(run, e, sepScratch)

  if (!best) {
    // No work: hover at mid-distance and refuse to fight. A support that never
    // swings is the clearest "priority target" signal the zone can give.
    e.tags.chanT = 0
    e.tags.hasWork = false
    const dx = run.player.pos.x - e.pos.x
    const dy = run.player.pos.y - e.pos.y
    const d = Math.hypot(dx, dy) || 1
    const sp = e.speed * speedMul
    let mx = 0
    let my = 0
    if (d < bandMin) {
      mx = -(dx / d)
      my = -(dy / d)
    } else if (d > bandMax) {
      pursue(run, e, dirScratch)
      mx = dirScratch.x
      my = dirScratch.y
    } else {
      const s = e.bob * 0.5
      mx = (-dy / d) * Math.sin(s)
      my = (dx / d) * Math.sin(s)
    }
    moveEnemy(run, e, (mx + sepScratch.x) * sp, (my + sepScratch.y) * sp, dt)
    if (Math.abs(dx) > 6) e.facing = dx < 0 ? -1 : 1
    return
  }

  e.tags.hasWork = true
  e.tags.tx = best.pos.x
  e.tags.ty = best.pos.y

  const dx = best.pos.x - e.pos.x
  const dy = best.pos.y - e.pos.y
  const d = Math.hypot(dx, dy) || 1

  if (d > tendRange + e.radius) {
    // THE SPRINT. It must read as an emergency across the room.
    e.tags.chanT = 0
    const sp = e.speed * sprintMul * speedMul
    moveEnemy(run, e, (dx / d + sepScratch.x * 0.6) * sp, (dy / d + sepScratch.y * 0.6) * sp, dt)
    e.facing = dx < 0 ? -1 : 1
    if (e.bob % 0.18 < dt) {
      emit(e.pos.x, e.pos.y + 6, { count: 2, color: '#c79bff', speed: 40, life: 0.35, size: 2, shape: 1 })
    }
    if (!state.saidTender) {
      state.saidTender = true
      run.bark('A gravetender. Something is racing you to the dead.')
    }
    return
  }

  // THE CHANNEL — telegraphed for its full length; interrupting is the counterplay
  let c = num(e.tags.chanT, 0)
  if (c <= 0) {
    c = R.tendChannel
    run.sfx('telegraph')
  }
  c -= dt
  e.tags.chanT = c
  e.telegraph = Math.max(e.telegraph, Math.min(0.6, Math.max(0, c)))
  best.tags.tendClaimT = run.time + 0.25
  best.tags.tendBy = tid
  moveEnemy(run, e, 0, 0, dt)

  if (c <= 0) {
    e.tags.chanT = 0
    best.tags.tended = true
    const remain = num(best.tags.huskT, 0)
    const t = Math.min(remain, R.tendedSeconds)
    best.tags.huskT = t
    best.tags.huskMax = Math.max(t, 0.001)
    e.cool = 0.4
    run.sfx('door')
    run.shake(2, 0.2)
    emit(best.pos.x, best.pos.y, {
      count: 18,
      color: '#c79bff',
      speed: 130,
      life: 0.5,
      lifeVar: 0.2,
      size: 3,
      shape: 1,
    })
  }
})

// ===========================================================================
// the mechanic
// ===========================================================================

const ROOM_BARKS = [
  'The Pale Court. Cut one down and then finish the job.',
  'Gravetenders walk here. They want the bodies, not you.',
  'Three sources, and every corpse an argument you have to win twice.',
]

export const zone3Mechanic: ZoneMechanic = {
  zoneId: 3,

  onZoneStart(ctx: RunCtx): void {
    state.ctx = ctx
    state.saidHusk = false
    state.saidTender = false
    state.saidReform = false
    tenderSeq = 0
    ctx.bark('The Pale Court. Nothing here stays where you put it.')
  },

  onRoomStart(ctx: RunCtx, room: Room): void {
    state.ctx = ctx
    // buildRoom wipes every pool before this runs, so no husk survives a room.
    state.huskCount = 0
    state.sealedThisRoom = 0
    state.reformedThisRoom = 0
    // Grave Candle (talisman, DESIGN §7.2): one free seal per room.
    state.candleReady = (ctx as RunLike).flags?.graveCandle === true
    if (room.isRespite || isArena(ctx)) return
    const line = ROOM_BARKS[Math.min(ROOM_BARKS.length - 1, room.index)]
    if (line) ctx.bark(line)
  },

  update(ctx: RunCtx, _dt: number): void {
    state.ctx = ctx
    let n = 0
    const list = ctx.enemies
    for (let i = 0; i < list.length; i++) {
      if (list[i].alive && list[i].def.id === HUSK) n++
    }
    state.huskCount = n
  },

  /**
   * The whole zone rule, in one hook.
   *   a husk died  -> it was hit -> that is a seal (the timer path kills silent,
   *                   and silent deaths never reach this hook)
   *   a body died  -> lay a husk, unless it was already a re-form
   */
  onEnemyDeath(ctx: RunCtx, e): void {
    state.ctx = ctx
    if (e.def.id === HUSK) {
      seal(ctx, e.pos.x, e.pos.y, e.tags.tended === true)
      return
    }
    if (e.tags.reformed === true) return
    if (R.huskSources.indexOf(e.def.id) < 0) return
    if (state.candleReady) {
      // The Grave Candle burns the first one for you.
      state.candleReady = false
      seal(ctx, e.pos.x, e.pos.y, false)
      ctx.bark('The grave candle takes that one.')
      return
    }
    layHusk(ctx, e.pos.x, e.pos.y, e.def.id, e.elite)
  },

  // ------------------------------------------------------------------ render

  /** Under the bodies: the husk's halo and countdown, and the tender's line. */
  renderUnder(d: Draw): void {
    const run = state.ctx
    if (!run) return
    const ctx = d.ctx
    const list = run.enemies
    const t = run.time

    for (let i = 0; i < list.length; i++) {
      const e = list[i]
      if (!e.alive) continue

      if (e.def.id === HUSK) {
        const max = Math.max(0.001, num(e.tags.huskMax, R.huskSeconds))
        const left = Math.max(0, num(e.tags.huskT, 0))
        const k = 1 - Math.max(0, Math.min(1, left / max))
        const tended = e.tags.tended === true
        // the last second turns amber: "it is coming back NOW"
        const urgent = left <= 1
        const glow = tended ? '#c79bff' : urgent ? '#ffbe5c' : '#8fd8ff'
        const rate = tended ? 13 : urgent ? 16 : 5
        const pulse = 0.55 + 0.45 * Math.sin(t * rate + e.pos.x * 0.05)

        // a pool of cold light on pale marble: the eye has to be dragged here,
        // so this is a hole of dark under a bright rim, not a soft glow
        ctx.globalAlpha = 0.34
        ctx.fillStyle = '#1b2233'
        ctx.beginPath()
        ctx.ellipse(e.pos.x, e.pos.y + 6, 26, 13, 0, 0, TAU)
        ctx.fill()
        ctx.globalAlpha = 0.2 + 0.22 * pulse
        ctx.fillStyle = glow
        ctx.beginPath()
        ctx.ellipse(e.pos.x, e.pos.y + 6, 32 + pulse * 6, 16 + pulse * 4, 0, 0, TAU)
        ctx.fill()
        ctx.globalAlpha = 0.3 + 0.3 * pulse
        ctx.beginPath()
        ctx.ellipse(e.pos.x, e.pos.y + 6, 16, 8, 0, 0, TAU)
        ctx.fill()

        // the countdown: a ring that closes as the body comes back
        ctx.globalAlpha = 0.55
        ctx.strokeStyle = '#0b0e18'
        ctx.lineWidth = 4
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y + 4, 19, 0, TAU)
        ctx.stroke()
        ctx.globalAlpha = 1
        ctx.strokeStyle = tended ? '#ff8a5c' : glow
        ctx.lineWidth = 3.5
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y + 4, 19, -Math.PI / 2, -Math.PI / 2 + TAU * k)
        ctx.stroke()
        ctx.globalAlpha = 1
        continue
      }

      if (e.def.id === TENDER && e.tags.hasWork === true) {
        const tx = num(e.tags.tx, e.pos.x)
        const ty = num(e.tags.ty, e.pos.y)
        const channelling = num(e.tags.chanT, 0) > 0
        ctx.globalAlpha = channelling ? 0.55 : 0.22
        ctx.strokeStyle = '#c79bff'
        ctx.lineWidth = channelling ? 2.5 : 1.5
        ctx.beginPath()
        ctx.moveTo(e.pos.x, e.pos.y)
        ctx.lineTo(tx, ty)
        ctx.stroke()
        ctx.globalAlpha = 1
      }
    }
  },

  /**
   * Over the bodies: the two tells that must never be hidden by a swarm — the
   * Courtier's red glint, and a chevron over every husk still owed a blow.
   */
  renderOver(d: Draw): void {
    const run = state.ctx
    if (!run) return
    const ctx = d.ctx
    const list = run.enemies
    const t = run.time

    for (let i = 0; i < list.length; i++) {
      // the runtime body carries the render fields the frozen Enemy shape does not
      const e = list[i] as EnemyEnt
      if (!e.alive) continue

      if (e.def.id === HUSK) {
        const bob = Math.sin(t * 4 + e.pos.y * 0.05) * 2
        const y = e.pos.y - 28 + bob
        const tended = e.tags.tended === true
        const urgent = num(e.tags.huskT, 0) <= 1
        ctx.globalAlpha = 0.85
        ctx.fillStyle = '#0b0e18'
        ctx.beginPath()
        ctx.moveTo(e.pos.x, y - 8)
        ctx.lineTo(e.pos.x + 8, y + 4)
        ctx.lineTo(e.pos.x - 8, y + 4)
        ctx.closePath()
        ctx.fill()
        ctx.globalAlpha = 1
        ctx.fillStyle = tended ? '#ff8a5c' : urgent ? '#ffbe5c' : '#8fd8ff'
        ctx.beginPath()
        ctx.moveTo(e.pos.x, y - 6)
        ctx.lineTo(e.pos.x + 6, y + 3)
        ctx.lineTo(e.pos.x - 6, y + 3)
        ctx.closePath()
        ctx.fill()
        continue
      }

      if (e.tags.glint === true && e.telegraph > 0) {
        // the red glint on the rapier: small, bright, unmistakable
        const k = 0.4 + 0.6 * Math.abs(Math.sin(t * 26))
        const gx = e.pos.x + e.facing * 11
        const gy = e.pos.y - 10
        ctx.globalAlpha = k
        ctx.fillStyle = '#ff4a4a'
        ctx.beginPath()
        ctx.moveTo(gx, gy - 5)
        ctx.lineTo(gx + 3.5, gy)
        ctx.lineTo(gx, gy + 5)
        ctx.lineTo(gx - 3.5, gy)
        ctx.closePath()
        ctx.fill()
        ctx.globalAlpha = 0.5 * k
        ctx.beginPath()
        ctx.arc(gx, gy, 8, 0, TAU)
        ctx.fill()
        ctx.globalAlpha = 1
      }
    }
  },

  onZoneEnd(_ctx: RunCtx): void {
    state.ctx = null
    state.candleReady = false
  },
}

// ===========================================================================
// sprites — chunky, palette-driven, cold. Nothing here shares a silhouette
// with zone 1: the court is tall, narrow and vertical where the Broken Gate
// was low and hunched.
// ===========================================================================

const RD = Math.round

function pstr(p: Record<string, number | string>, k: string, d: string): string {
  const v = p[k]
  return typeof v === 'string' ? v : d
}

/** The violet rift every generator carries so "source" reads at a glance. */
function gloomMaw(a: PixelArt, cx: number, cy: number, r: number): void {
  a.ellipse(cx, cy, r, r * 0.75, '#150c22')
  a.ellipse(cx, cy + r * 0.06, r * 0.68, r * 0.5, '#5b2f92')
  a.ellipse(cx, cy + r * 0.12, r * 0.36, r * 0.26, '#a774e8')
}

registerPainter('z3_courtier', (a, p) => {
  const s = a.size
  const cx = s / 2
  const cloth = pstr(p, 'cloth', '#3b3550')
  const bone = pstr(p, 'bone', '#e8e4d4')
  const glint = pstr(p, 'glint', '#ff4a4a')
  const clothLo = shade(cloth, -0.4)
  const clothHi = shade(cloth, 0.28)
  const boneLo = shade(bone, -0.26)

  // cape thrown back off the left shoulder — the duelist's read
  a.ellipse(cx - s * 0.22, s * 0.56, s * 0.16, s * 0.26, clothLo)
  // long legs in pale hose: tall and narrow, the opposite of zone 1's crouch
  a.rect(RD(cx - s * 0.1), RD(s * 0.7), 3, RD(s * 0.2), boneLo)
  a.rect(RD(cx + s * 0.04), RD(s * 0.7), 3, RD(s * 0.2), boneLo)
  a.hline(RD(cx - s * 0.14), RD(s * 0.9), 5, clothLo)
  a.hline(RD(cx + s * 0.02), RD(s * 0.9), 5, clothLo)
  // court coat
  a.rect(RD(cx - s * 0.16), RD(s * 0.5), RD(s * 0.32), RD(s * 0.24), cloth)
  a.vline(RD(cx), RD(s * 0.52), RD(s * 0.2), clothLo)
  a.hline(RD(cx - s * 0.16), RD(s * 0.5), RD(s * 0.32), clothHi)
  // bone cuirass
  a.ellipse(cx, s * 0.45, s * 0.19, s * 0.13, bone)
  a.ellipse(cx, s * 0.48, s * 0.11, s * 0.07, boneLo)
  // THE RUFF: a wide pale collar. Recognisable at forty pixels.
  a.ellipse(cx, s * 0.33, s * 0.27, s * 0.07, bone)
  a.ellipse(cx, s * 0.315, s * 0.2, s * 0.04, shade(bone, 0.22))
  // narrow skull under a court wig-crest
  a.ellipse(cx, s * 0.2, s * 0.12, s * 0.13, bone)
  a.ellipse(cx, s * 0.12, s * 0.09, s * 0.05, boneLo)
  a.rect(RD(cx - s * 0.09), RD(s * 0.19), RD(s * 0.18), 2, '#191524')
  a.set(RD(cx - s * 0.07), RD(s * 0.19), glint)
  a.set(RD(cx + s * 0.05), RD(s * 0.19), glint)
  // the rapier: long, thin, low
  const wx = RD(cx + s * 0.34)
  a.vline(wx, RD(s * 0.26), RD(s * 0.44), '#c9cfdd')
  a.vline(wx + 1, RD(s * 0.3), RD(s * 0.36), shade('#c9cfdd', -0.3))
  a.set(wx, RD(s * 0.25), glint)
  a.hline(wx - 2, RD(s * 0.7), 5, clothHi)
})

registerPainter('z3_wailer', (a, p) => {
  const s = a.size
  const cx = s / 2
  const shroud = pstr(p, 'shroud', '#4a4560')
  const veil = pstr(p, 'veil', '#ded8c6')
  const grief = pstr(p, 'grief', '#a8e878')
  const shroudLo = shade(shroud, -0.38)

  // it hovers: torn wisps instead of feet
  for (let i = -2; i <= 2; i++) {
    const x = RD(cx + i * s * 0.09)
    const h = RD(s * (0.1 + 0.05 * ((i + 3) % 3)))
    a.vline(x, RD(s * 0.78), h, i % 2 === 0 ? shroudLo : shroud)
  }
  // the bell of the shroud
  for (let y = 0; y < RD(s * 0.44); y++) {
    const t = y / (s * 0.44)
    const w = RD(s * (0.07 + 0.24 * t))
    a.hline(RD(cx - w), RD(s * 0.36 + y), w * 2, y % 6 === 5 ? shroudLo : shroud)
  }
  // hanging sleeves
  a.ellipse(cx - s * 0.28, s * 0.56, s * 0.06, s * 0.16, shroudLo)
  a.ellipse(cx + s * 0.28, s * 0.56, s * 0.06, s * 0.16, shroudLo)
  // wide mourner's veil
  a.ellipse(cx, s * 0.28, s * 0.22, s * 0.2, veil)
  a.ellipse(cx, s * 0.22, s * 0.15, s * 0.12, shade(veil, 0.18))
  a.ellipse(cx, s * 0.34, s * 0.14, s * 0.1, shade(veil, -0.22))
  // the grief: a vertical open mouth, and nothing else of a face
  a.rect(RD(cx - 1), RD(s * 0.28), 2, RD(s * 0.14), '#171420')
  a.rect(RD(cx - 1), RD(s * 0.3), 2, RD(s * 0.09), grief)
  // the ring motif, hanging above the veil
  a.ring(cx, s * 0.08, s * 0.1, grief)
})

registerPainter('z3_gravetender', (a, p) => {
  const s = a.size
  const cx = s / 2
  const robe = pstr(p, 'robe', '#4c4256')
  const bone = pstr(p, 'bone', '#ded8c6')
  const lamp = pstr(p, 'lamp', '#c79bff')
  const robeLo = shade(robe, -0.38)
  const robeHi = shade(robe, 0.22)

  // hunched: the mass sits low and leans forward, unlike every other body here
  a.ellipse(cx, s * 0.86, s * 0.3, s * 0.08, robeLo)
  a.ellipse(cx, s * 0.68, s * 0.3, s * 0.2, robe)
  a.ellipse(cx - s * 0.04, s * 0.52, s * 0.26, s * 0.17, robeHi) // the hump of the back
  a.ellipse(cx - s * 0.04, s * 0.5, s * 0.18, s * 0.1, robe)
  // hood, pointed down and forward: it is looking at the floor, not at you
  a.ellipse(cx + s * 0.16, s * 0.42, s * 0.15, s * 0.13, robe)
  a.ellipse(cx + s * 0.2, s * 0.46, s * 0.09, s * 0.07, '#15111d')
  a.set(RD(cx + s * 0.2), RD(s * 0.46), lamp)
  a.set(RD(cx + s * 0.23), RD(s * 0.47), lamp)
  // the spade across its back
  a.line(RD(cx - s * 0.3), RD(s * 0.3), RD(cx + s * 0.06), RD(s * 0.66), '#6b5236')
  a.ellipse(cx - s * 0.32, s * 0.27, s * 0.07, s * 0.09, bone)
  // the lantern it works by
  a.vline(RD(cx + s * 0.3), RD(s * 0.5), RD(s * 0.1), '#6b5236')
  a.disc(cx + s * 0.3, s * 0.64, s * 0.08, '#241c30')
  a.disc(cx + s * 0.3, s * 0.64, s * 0.05, lamp)
  a.set(RD(cx + s * 0.3), RD(s * 0.64), '#ffffff')
})

registerPainter('z3_husk', (a, p) => {
  const s = a.size
  const cx = s / 2
  const glow = pstr(p, 'glow', '#dfe9ff')
  const core = pstr(p, 'core', '#6f7ba0')

  // The trick that makes it read as an OUTLINE and not a body: paint the whole
  // silhouette in light, then paint it again one pixel smaller in cold dark.
  // What survives is a rim — a hollow shape where someone used to be.
  a.ellipse(cx, s * 0.56, s * 0.24, s * 0.28, glow)
  a.ellipse(cx, s * 0.26, s * 0.16, s * 0.16, glow)
  a.ellipse(cx - s * 0.26, s * 0.5, s * 0.07, s * 0.13, glow)
  a.ellipse(cx + s * 0.26, s * 0.5, s * 0.07, s * 0.13, glow)

  a.ellipse(cx, s * 0.56, s * 0.19, s * 0.23, core, 0.82)
  a.ellipse(cx, s * 0.26, s * 0.11, s * 0.11, core, 0.82)
  a.ellipse(cx - s * 0.26, s * 0.5, s * 0.04, s * 0.09, core, 0.82)
  a.ellipse(cx + s * 0.26, s * 0.5, s * 0.04, s * 0.09, core, 0.82)

  // two pale eyes: it is already looking, which is what makes it a job
  a.set(RD(cx - s * 0.06), RD(s * 0.25), glow)
  a.set(RD(cx + s * 0.04), RD(s * 0.25), glow)

  // motes rising off it — the thing that is coming back
  a.set(RD(cx - s * 0.12), RD(s * 0.1), glow)
  a.set(RD(cx + s * 0.1), RD(s * 0.05), glow)
  a.set(RD(cx + s * 0.02), RD(s * 0.14), glow)
})

registerPainter('z3_gen_reliquary', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const bone = pstr(p, 'bone', '#ece7d6')
  const cloth = pstr(p, 'cloth', '#5a3f6e')
  const boneLo = shade(bone, -0.3)
  const stone = shade(pal.wall, -0.18)

  // plinth
  a.ellipse(cx, s * 0.9, s * 0.4, s * 0.08, shade(stone, -0.3))
  a.rect(RD(cx - s * 0.3), RD(s * 0.72), RD(s * 0.6), RD(s * 0.18), stone)
  a.hline(RD(cx - s * 0.3), RD(s * 0.72), RD(s * 0.6), shade(stone, 0.25))
  // the reliquary casket, standing on end like a coffin
  a.rect(RD(cx - s * 0.21), RD(s * 0.24), RD(s * 0.42), RD(s * 0.5), bone)
  a.rect(RD(cx - s * 0.21), RD(s * 0.24), RD(s * 0.42), 3, shade(bone, 0.24))
  a.vline(RD(cx - s * 0.21), RD(s * 0.24), RD(s * 0.5), boneLo)
  a.vline(RD(cx + s * 0.2), RD(s * 0.24), RD(s * 0.5), boneLo)
  // gabled lid + funeral drape
  for (let y = 0; y < RD(s * 0.14); y++) {
    const w = RD(s * (0.05 + 0.16 * (y / (s * 0.14))))
    a.hline(RD(cx - w), RD(s * 0.12 + y), w * 2, y % 4 === 3 ? boneLo : bone)
  }
  a.rect(RD(cx - s * 0.24), RD(s * 0.4), RD(s * 0.48), RD(s * 0.07), cloth)
  a.hline(RD(cx - s * 0.24), RD(s * 0.4), RD(s * 0.48), shade(cloth, 0.3))
  // candles at the corners
  a.vline(RD(cx - s * 0.3), RD(s * 0.56), RD(s * 0.16), bone)
  a.vline(RD(cx + s * 0.29), RD(s * 0.56), RD(s * 0.16), bone)
  a.set(RD(cx - s * 0.3), RD(s * 0.54), '#dce7ff')
  a.set(RD(cx + s * 0.29), RD(s * 0.54), '#dce7ff')
  gloomMaw(a, cx, s * 0.58, s * 0.13)
})

registerPainter('z3_gen_boneorgan', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const bone = pstr(p, 'bone', '#e4dfcc')
  const pipe = pstr(p, 'pipe', '#b9b2a0')
  const stone = shade(pal.wall, -0.22)

  // base
  a.ellipse(cx, s * 0.9, s * 0.42, s * 0.08, shade(stone, -0.32))
  a.rect(RD(cx - s * 0.36), RD(s * 0.66), RD(s * 0.72), RD(s * 0.24), stone)
  a.hline(RD(cx - s * 0.36), RD(s * 0.66), RD(s * 0.72), shade(stone, 0.3))
  // a rank of femur pipes in a wave — unmistakable against a casket or a slab
  const n = 7
  for (let i = 0; i < n; i++) {
    const x = RD(cx - s * 0.3 + (i * s * 0.6) / (n - 1))
    const h = RD(s * (0.22 + 0.24 * Math.sin((i / (n - 1)) * Math.PI)))
    a.rect(x - 1, RD(s * 0.66) - h, 4, h, i % 2 === 0 ? bone : pipe)
    a.rect(x - 1, RD(s * 0.66) - h, 4, 2, shade(bone, 0.3))
    a.disc(x + 0.5, RD(s * 0.66) - h - 1, 2.2, bone)
  }
  gloomMaw(a, cx, s * 0.76, s * 0.14)
})

registerPainter('z3_gen_crypt', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const stone = pstr(p, 'stone', '#cfcbba')
  const lo = shade(stone, -0.34)
  const hi = shade(stone, 0.26)
  const dirt = shade(pal.floor, -0.42)

  // a floor slab, shoved aside and cracked open
  a.ellipse(cx, s * 0.72, s * 0.46, s * 0.2, dirt)
  a.ellipse(cx, s * 0.66, s * 0.42, s * 0.18, stone)
  a.ellipse(cx, s * 0.62, s * 0.36, s * 0.14, hi)
  // the crack, jagged, running the long way
  a.line(RD(cx - s * 0.34), RD(s * 0.66), RD(cx - s * 0.1), RD(s * 0.58), lo)
  a.line(RD(cx - s * 0.1), RD(s * 0.58), RD(cx + s * 0.08), RD(s * 0.7), lo)
  a.line(RD(cx + s * 0.08), RD(s * 0.7), RD(cx + s * 0.34), RD(s * 0.6), lo)
  // the lid, tipped up on edge behind it
  a.rect(RD(cx - s * 0.26), RD(s * 0.22), RD(s * 0.5), RD(s * 0.24), stone)
  a.hline(RD(cx - s * 0.26), RD(s * 0.22), RD(s * 0.5), hi)
  a.hline(RD(cx - s * 0.24), RD(s * 0.3), RD(s * 0.46), lo)
  a.hline(RD(cx - s * 0.24), RD(s * 0.38), RD(s * 0.46), lo)
  gloomMaw(a, cx, s * 0.64, s * 0.14)
})

registerPainter('z3_grief', (a, p) => {
  const s = a.size
  const cx = s / 2
  const color = pstr(p, 'color', '#a8e878')
  // a shard of a ring, not a bolt: it is a piece of an expanding wall
  a.ellipse(cx, cx, s * 0.42, s * 0.2, shade(color, -0.3))
  a.ellipse(cx, cx, s * 0.3, s * 0.13, color)
  a.ellipse(cx, cx, s * 0.14, s * 0.07, shade(color, 0.45))
})

// ---------------------------------------------------------------- registry
// registerZoneContent bakes recipes for enemies and generators; a projectile's
// sprite has to be registered by hand (game/zones.ts only wires the def).

registerRecipe(ZONE3_PROJECTILES[0].id, {
  key: 'z3_grief',
  size: 17,
  params: { color: ZONE3_PROJECTILES[0].color },
})

registerZoneContent({
  enemies: [...ZONE3_ENEMIES, ...ZONE3_REFORMED],
  projectiles: ZONE3_PROJECTILES,
  generators: ZONE3_GENERATORS,
})
registerZone(ZONE3, zone3Mechanic)
